import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Dropdown from 'components/Form/Dropdown';
import FormResult from 'components/Form/FormResult';
import SubmitButton from 'components/Form/SubmitButton';
import Notification from 'components/Notification';
import Panel from 'components/Panel';
import {ClientInboxItemBackend} from 'pages/Portal/ClientInboxItem/types';
import {
    ReceiveDocumentFormData,
    usePostReceiveDocument,
} from 'pages/Portal/services';
import {useBackend} from 'state/backend';
import {FormFields} from 'types/utils';
import {onApiFormError} from 'utils/request/result';

type FormData = FormFields<ReceiveDocumentFormData>;

const ReceiveDocumentPanel: FC = () => {
    const {
        data: {
            canBeReceived,
            document,
            loggedInPartner,
            portalMid,
            receivablePartners,
        },
    } = useBackend<ClientInboxItemBackend>();
    const {t} = useTranslation('Portal');

    const methods = useForm<FormData>();
    const {handleSubmit} = methods;
    const receiveDocument = usePostReceiveDocument();
    const [isLoading, setIsLoading] = useState(false);

    const onAddToSecureInbox = ({receivingPartner}: FormData) => {
        setIsLoading(true); // loading while being redirected to secureinbox;
        const formData = {
            receivingPartner:
                receivablePartners?.length === 1
                    ? receivablePartners[0].mid
                    : receivingPartner,
        };

        receiveDocument
            .mutateAsync({documentMid: document.mid, formData, portalMid})
            .catch((error) => {
                onApiFormError(methods)(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    if (
        !loggedInPartner ||
        !receivablePartners ||
        receivablePartners.length === 0
    ) {
        return null;
    }

    const partnerOptions = receivablePartners
        ? receivablePartners.map(({displayName, mid}) => ({
              label: displayName,
              value: mid,
          }))
        : [];

    return (
        <Panel title={t('inbox.addToSecureInboxTitle')}>
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit((formData) =>
                        onAddToSecureInbox(formData),
                    )}
                >
                    <div className="flex gap-y-2 flex-col justify-between">
                        {canBeReceived ? (
                            <>
                                {receivablePartners.length === 1 ? (
                                    <>
                                        <div className="text-grey-500 text-sm">
                                            {t('inbox.company')}
                                        </div>
                                        <div>
                                            {receivablePartners[0].displayName}
                                        </div>
                                    </>
                                ) : (
                                    <Dropdown
                                        aria-label="receivingPartner"
                                        label={t('inbox.company')}
                                        name="receivingPartner"
                                        options={partnerOptions}
                                        required={true}
                                        type="select"
                                    />
                                )}
                                <SubmitButton
                                    className="w-full mt-2"
                                    icon="inbox"
                                    isLoading={isLoading}
                                    kind="secondary"
                                    onClick={() => {}}
                                    size="base"
                                >
                                    {t('inbox.addToSecureInbox')}
                                </SubmitButton>
                            </>
                        ) : (
                            <div className="mt-2">
                                <Notification type="alert">
                                    {t('inbox.unsupportedDocument')}
                                </Notification>
                            </div>
                        )}
                        <FormResult />
                    </div>
                </form>
            </FormProvider>
        </Panel>
    );
};

export default ReceiveDocumentPanel;
